import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Paragraph from "antd/es/typography/Paragraph";
import OverlaySpiner from "../../../components/OverlaySpinner/OverlaySpinner";
import {
  getAgreementConfig,
  getSupplierSettings,
} from "../../../Redux/Selectors/appConfigSelector";
import {
  transactionRequestActionRequest,
  transactionRequestActionReset,
} from "../redux/paymentActions";
import { getTransactionRequestState } from "../redux/paymentSelectors";
import { cardStatus, getTransactionRequestAction } from "../utils";
import PaymentMethodSelection from "./PaymentMethodSelection";
import PaymentResult from "./PaymentResult";

/**
 * Reserva con total $0: no hay nada que pagar, solo se solicita la reserva.
 * Sin términos y condiciones se solicita sola; con términos, al aceptarlos.
 */
const ZeroTotalBooking = ({
  transactionId,
  renderLayout,
  ...selectionProps
}) => {
  const { t } = useTranslation("result");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const supplierSettings = useSelector(getSupplierSettings);
  const { enabled: agreementEnabled } = useSelector(
    getAgreementConfig,
    shallowEqual
  );
  const { loading, success, error } = useSelector(
    getTransactionRequestState,
    shallowEqual
  );
  const autoRequested = useRef(false);

  const requestBooking = () => {
    dispatch(
      transactionRequestActionRequest(
        transactionId,
        getTransactionRequestAction(supplierSettings)
      )
    );
  };

  useEffect(() => {
    if (agreementEnabled || autoRequested.current) return;
    autoRequested.current = true;
    requestBooking();
  }, [agreementEnabled]);

  useEffect(() => {
    return () => {
      dispatch(transactionRequestActionReset());
    };
  }, []);

  if (success) {
    return renderLayout(
      <PaymentResult
        status={cardStatus.PENDING}
        title={t("bookingRequestedTitle")}
        buttonText={t("common:continue")}
        onClick={() => navigate(`/`)}
      >
        <Paragraph>{t("bookingRequestedDescription")}</Paragraph>
      </PaymentResult>
    );
  }

  if (error) {
    return renderLayout(
      <PaymentResult
        status={cardStatus.ERROR}
        title={t("bookingRequestErrorTitle")}
        buttonText={t("retryBookingRequest")}
        onClick={requestBooking}
      >
        <Paragraph>{t("bookingRequestErrorDescription")}</Paragraph>
      </PaymentResult>
    );
  }

  if (!agreementEnabled) {
    return <OverlaySpiner isLoading={true} />;
  }

  return renderLayout(
    <PaymentMethodSelection
      {...selectionProps}
      hidePaymentMethods
      loading={loading}
      onContinue={requestBooking}
    />
  );
};

ZeroTotalBooking.propTypes = {
  transactionId: PropTypes.string.isRequired,
  renderLayout: PropTypes.func.isRequired,
};

export default ZeroTotalBooking;
